(ns app.front.view.application-wizard.step-tracker
  "Application wizard step tracker component.
   Mobile view shows a collapsible panel, desktop view shows a horizontal stepper.
   Completed steps are clickable, allowing users to jump back to previous steps."
  (:require ["@carbon/icons-react/es/task/16" :default Task16]
            [app.front.util :refer [for-frag for-all-indexed]]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.icons :as i]
            [core.front.media-query :as media-query]
            [core.util.util :as util]
            [reagent.core :as r]))

(defn- step-clickable? [steps index step-completed-sub step-reachable-sub step-locked-sub]
  (let [reachable-sub            (or step-reachable-sub step-completed-sub)
        path                     (-> steps (nth index) :path)
        reachable?               (<sub [reachable-sub path])
        previous-step-reachable? (when (pos? index)
                                   (<sub [reachable-sub (-> steps (nth (dec index)) :path)]))]
    ;; A locked step is never clickable, not even via the forward-click affordance
    ;; (previous-step-reachable?) that normally lets the user jump to the next step.
    (and (not (and step-locked-sub (<sub [step-locked-sub path])))
         (or reachable? previous-step-reachable?))))

(defn- enrich-steps
  "Attaches :index, :status and :on-click to each step.

   Steps after the current step are always shown as :upcoming.
   Steps before the current step are :completed or :incomplete based on data."
  [{:keys [steps step-completed-sub step-incomplete-sub step-reachable-sub step-locked-sub
           current-step-index open-step-evt]}]
  (for-all-indexed [[index {:keys [path] :as step}] steps]
    (let [completed?  (<sub [step-completed-sub path])
          incomplete? (and step-incomplete-sub (<sub [step-incomplete-sub path]))]
      (assoc step
             :index    index
             :status   (cond
                         (= index current-step-index)
                         :current

                         (> index current-step-index)
                         :upcoming

                         incomplete?
                         :incomplete

                         completed?
                         :completed

                         :else
                         :upcoming)
             :on-click (when (step-clickable? steps index step-completed-sub step-reachable-sub step-locked-sub)
                         #(>evt [open-step-evt {:step-path path}]))))))

(defn- step-circle [status tr]
  [:div.w-8.h-8.rounded-full.flex.items-center.justify-center
   {:class (case status
             :current
             "bg-yellow-300 border-transparent"

             :completed
             "bg-green-600 border-transparent"

             :incomplete
             "bg-status-attention border-transparent"

             :upcoming
             "border border-dashed border-gray-400")}
   (case status
     :current
     (cc/icon {:icon        i/pen
               :description (tr [:application-wizard/current-step])
               :fill        "black"
               :height      "12px"
               :width       "12px"})

     :completed
     (cc/icon {:icon        i/check
               :description (tr [:application-wizard/completed-step])
               :fill        "white"
               :height      "12px"
               :width       "12px"})

     :incomplete
     [:> Task16 {:aria-label (tr [:application-wizard/incomplete-step])
                 :fill       "black"
                 :height     "12px"
                 :width      "12px"}]

     nil)])

(defn- step-indicator [{:keys [status on-click label-tr]} {:keys [desktop? tr]}]
  [:button.flex.items-center.gap-2.bg-transparent.border-none
   (merge {:class (if desktop?
                    "flex-col"
                    "w-full px-4 py-3")}
          (if on-click
            (util/mmap on-click)
            {:disabled true}))
   [step-circle status tr]
   [:span.text-sm
    {:class ["font-semibold"
             (if (#{:current :completed :incomplete} status)
               "text-gray-900"
               "text-gray-300")]}
    (tr label-tr)]])

(defn- mobile-header [{:keys [current-step-index steps on-click tr]}]
  (let [label-tr (-> steps (nth current-step-index) :label-tr)]
    [:button.flex.items-center.w-full.gap-6.px-6.py-3.border-none.bg-transparent.rounded-t
     (util/mmap on-click)
     (cc/icon {:icon        i/chevron-down
               :description (tr [:application-wizard/expand-steps])
               :width       "12px"
               :height      "12px"})
     [:div.flex.items-center.gap-2
      [step-circle :current tr]
      [:span.text-sm.font-semibold.text-gray-900
       (tr label-tr)]]]))

(defn- mobile-expanded-panel [{:keys [steps on-collapse tr]}]
  [:<>
   [:div.fixed.inset-0.z-10
    {:class    "bg-black/30"
     :on-click on-collapse}]
   [:div.absolute.py-1.top-0.right-0.left-0.z-20.bg-white.rounded.space-y-1
    (for-frag [{:keys [on-click] :as step} steps]
      [step-indicator
       (assoc step :on-click (when on-click
                               (fn []
                                 (on-click)
                                 (on-collapse))))
       (util/mmap tr)])]])

(defn- mobile-step-tracker [{:keys [steps current-step-index tr]}]
  (r/with-let [*expanded? (r/atom false)]
    [:div.relative
     (if @*expanded?
       [mobile-expanded-panel (util/mmap {:on-collapse #(reset! *expanded? false)}
                                         steps tr)]
       [mobile-header (util/mmap {:on-click #(swap! *expanded? not)}
                                 current-step-index steps tr)])]))

(defn- desktop-step-tracker [{:keys [steps tr]}]
  (let [last-index (dec (count steps))]
    [:div.flex.justify-between.px-4.lg:px-10.py-6
     (for-frag [{:keys [index] :as step} steps]
       [:<>
        [step-indicator step (util/mmap {:desktop? true}
                                        tr)]
        (when (< index last-index)
          [:div.w-4.self-center.bg-gray-400
           {:class "h-[1px]"}])])]))

(defn step-tracker
  "Props:
    - steps: list of steps with :path and :label-tr
    - current-step-index: index of the current step
    - step-completed-sub: sub taking a step path, returns boolean for visual completed status
    - step-incomplete-sub: (optional) sub taking a step path, returns boolean for attention indicator
    - step-reachable-sub: (optional) sub taking a step path, returns boolean for clickability;
                          defaults to step-completed-sub when not provided
    - step-locked-sub: (optional) sub taking a step path, returns boolean; a locked step is
                       never clickable, even via the forward-click affordance
   - open-step-evt: event taking a map with step path and navigating to that step"
  [props]
  (let [desktop?       (<sub [::media-query/breakpoint-matches? :md])
        enriched-steps (enrich-steps props)
        enriched-props (assoc props :steps enriched-steps)]
    (if desktop?
      [desktop-step-tracker enriched-props]
      [mobile-step-tracker enriched-props])))
